import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User } from '../../../models/user';
import { YshPageHeaderComponent } from "../../../components/ysh-page-header/ysh-page-header.component";
import { YshPanelComponent } from '../../../components/ysh-panel/ysh-panel.component';
import { YshButtonComponent } from '../../../components/ysh-button/ysh-button.component';
import { Trait, YshTraitsComponent } from '../../../components/ysh-traits/ysh-traits.component';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { AppRoutes } from '../../../app.routes';
import { Affilation, Role } from '../../../models/affiliation';
import { AffiliationService } from '../../../services/api/affiliation/affiliation.service';
import { CompanyService } from '../../../services/api/company/company.service';
import { PermissionService, Resource } from '../../../services/permission/permission.service';
import { ToastService } from '../../../services/ui/toast/toast.service';
import { first } from 'rxjs';

@Component({
    selector: 'app-company-member',
    templateUrl: './company-member.component.html',
    styleUrl: './company-member.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, FormsModule, YshPageHeaderComponent, YshPanelComponent, YshButtonComponent, YshTraitsComponent, MatIconModule, MatFormFieldModule, MatSelectModule]
})
export class CompanyMemberComponent implements OnInit {
  member: User;
  memberInfo?: Trait[];
  affiliation?: Affilation;
  companyUid!: string;
  backLinkRoute: string;

  assignableRoles: Role[] = [];
  selectedRole?: Role;
  savingRole = false;

  constructor(
    private affiliationService: AffiliationService,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private permissionService: PermissionService,
    private toast: ToastService,
  ) {}

  ngOnInit() {
    this.getCompany();
    this.getMemberInfo();
  }

  getCompany() {
    this.companyService.currentCompany$.pipe(first()).subscribe(company => {
      this.companyUid = company.uid;
      this.backLinkRoute = `/${AppRoutes.Companies}/${company.uid}`;
      this.permissionService.getPermissions(company, [Resource.Affiliation]).subscribe(permissions => {
        this.assignableRoles = permissions.assignableRoles(Resource.Affiliation);
      });
    });
  }

  getMemberInfo() {
    const memberUid = this.route.snapshot.paramMap.get('memberUid');
    memberUid && this.affiliationService.get(memberUid).subscribe(affiliation => {
      this.affiliation = affiliation;
      this.selectedRole = affiliation?.role;
      this.memberInfo = [
        { label: 'Name', value: affiliation?.userName || 'None' },
        { label: 'Phone Number', value: affiliation?.userPhoneFormatted || 'None' },
        { label: 'Email Address', value: affiliation?.userEmail || 'None'},
        { label: 'Account Role', value: affiliation?.roleFormatted || 'None'},
      ];
    });
  }

  // Owner is intentionally omitted — the owner role cannot be assigned here.
  get roles(): Role[] {
    return this.assignableRoles.filter(role => role !== Role.Owner);
  }

  get canEditRole(): boolean {
    return !!this.affiliation?.permittedActions?.update && this.roles.length > 0;
  }

  get hasRoleChange(): boolean {
    return !!this.affiliation && !!this.selectedRole && this.selectedRole !== this.affiliation.role;
  }

  handleSaveRole() {
    const affiliation = this.affiliation;
    if (!affiliation || !this.canEditRole || !this.selectedRole || this.savingRole || !this.hasRoleChange) {
      return;
    }
    this.savingRole = true;
    this.affiliationService.update(affiliation.uid, { role: this.selectedRole }).subscribe({
      next: (affiliation) => {
        this.affiliation = affiliation;
        this.selectedRole = affiliation.role;
        this.memberInfo = this.memberInfo?.map(trait =>
          trait.label === 'Account Role'
            ? { ...trait, value: affiliation.roleFormatted || 'None' }
            : trait
        );
        this.savingRole = false;
        this.toast.show('success', 'Member role updated.');
      },
      error: () => {
        this.savingRole = false;
        this.toast.show('error', 'Unable to update member role.');
      },
    });
  }
}
